import { mapPersonType, useFindPersonDetailsSuspense } from '@api';
import { TouringCard, TouringCardHeader, TouringCardTitle } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextareaField } from '@hypeit-gmbh/hypeit-ui';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Box, Button, Group } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/persons/$id.general';
import {
  PersonNotesFormValuesType,
  usePersonNotesSchema,
  useUpdatePersonNotes,
} from './hooks';

export const PersonNotes: React.FC = () => {
  const { t } = useTranslation('personDetailsGeneral');
  const { id } = Route.useParams();
  const { data: personDetails } = useFindPersonDetailsSuspense(id, {
    query: { select: (data) => mapPersonType(data.data) },
  });

  const { hasPermission } = usePermissionContext();
  const { schema, defaultValues } = usePersonNotesSchema(personDetails);
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { updatePersonNotes } = useUpdatePersonNotes(personDetails.id);

  const onSubmit = async (data: PersonNotesFormValuesType) => {
    await updatePersonNotes(data);
  };

  const userCanEditPerson = hasPermission({
    permission: 'ADDRESSBOOK_PERSONS',
    action: 'WRITE',
  });

  return (
    <TouringCard>
      <TouringCardHeader>
        <TouringCardTitle>{t('NOTES.TITLE')}</TouringCardTitle>
      </TouringCardHeader>
      <Box pt="md">
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TextareaField<PersonNotesFormValuesType>
              fieldName="notes"
              placeholder={t('NOTES.PLACEHOLDER')}
              rows={10}
            />
            {userCanEditPerson && (
              <Group justify="flex-end" py="md">
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isDirty || form.formState.isSubmitting
                  }
                  loading={form.formState.isSubmitting}
                >
                  {t('SAVE_BUTTON', { ns: 'common' })}
                </Button>
              </Group>
            )}
          </form>
        </FormProvider>
      </Box>
    </TouringCard>
  );
};
